import { Link } from "react-router-dom";
import { ClipboardList, AlertTriangle, Hammer } from "lucide-react";
import { usePageMeta } from "@/lib/seo";
import { PalletFinalSale } from "@/components/PalletFinalSale";

const Card = ({ icon: Icon, title, tagline, bullets, warning, cta, to, testId, gold }) => (
  <div className="border-2 border-[#0A1C40] bg-white flex flex-col" data-testid={testId}>
    <div className="bg-[#0A1C40] text-white px-5 py-4">
      <Icon className="w-6 h-6 text-[#FFC220]" />
      <div className="font-display font-black text-xl mt-2 tracking-tight">{title}</div>
      <div className="text-sm text-white/70">{tagline}</div>
    </div>
    <ul className="p-5 space-y-1.5 text-sm flex-1">
      {bullets.map((b, i) => (
        <li key={i} className="flex gap-2"><span className="text-[#C9971E] font-black">·</span>{b}</li>
      ))}
    </ul>
    {warning && (
      <div className="mx-5 mb-4 bg-amber-100 border-2 border-amber-500 text-amber-900 text-xs font-black px-3 py-2" data-testid={`${testId}-warning`}>
        {warning}
      </div>
    )}
    <div className="p-5 pt-0">
      <Link
        to={to}
        className={`block text-center font-black py-3 rounded-full text-sm transition-colors ${
          gold ? "bg-[#FFC220] text-[#0A1C40] hover:bg-[#0A1C40] hover:text-[#FFC220]" : "bg-[#0A1C40] text-white hover:bg-black"
        }`}
        data-testid={`${testId}-cta`}
      >
        {cta}
      </Link>
      <Link to="/policies/pallets" className="block text-center text-xs text-gray-500 underline mt-2">
        View Pallet Sales Policy
      </Link>
    </div>
  </div>
);

export default function PalletSalesPage() {
  usePageMeta(
    "Pallet Sales — Manifested, Unmanifested & Custom Build | IPVALUE Marketplace",
    "Buy wholesale pallets in Grand Rapids, MI: manifested pallets, low-cost unmanifested pallets, or build your own custom pallet."
  );
  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-8 py-10" data-testid="pallet-sales-page">
      <div className="ipv-eyebrow text-[#C9971E]">Wholesale · Pallet Sales</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-[#0A1C40] mt-1">
        Three ways to buy a pallet.
      </h1>

      <div className="grid md:grid-cols-3 gap-4 mt-8">
        <Card
          icon={ClipboardList}
          title="MANIFESTED"
          tagline="Know what you're buying."
          testId="pallet-card-manifested"
          bullets={[
            "Manifest included",
            "Pre-built and ready to go",
            "Merchandise reviewed/inspected as represented by our pallet policy",
            "Pay the listed pallet price — no extra fees",
            "Want one item swapped? $99 swap fee per order",
          ]}
          cta="SHOP MANIFESTED PALLETS"
          to="/pallets/manifested"
        />
        <Card
          icon={AlertTriangle}
          title="UNMANIFESTED"
          tagline="Lower cost. Higher risk. Sold as-is."
          testId="pallet-card-unmanifested"
          bullets={[
            "Lower-cost option",
            "No complete manifest",
            "Contents and condition may be unknown",
            "Sold AS-IS · FINAL SALE",
            "No returns, exchanges, refunds, or guarantee unless specifically stated in writing",
          ]}
          warning="⚠️ UNMANIFESTED PALLETS ARE PURCHASED AT THE BUYER'S RISK."
          cta="SHOP UNMANIFESTED PALLETS"
          to="/pallets/unmanifested"
        />
        <Card
          icon={Hammer}
          title="CUSTOMIZE YOUR OWN"
          tagline="You pick the items. We build it."
          testId="pallet-card-custom"
          bullets={[
            "Merchandise INCLUDED in the pallet price",
            "3 items $500 · 5 items $900 · 7 items $1,500 · 11 items $2,200",
            "Plus a flat $299 customization fee — charged together at checkout",
            "Pick from live in-stock availability with photos",
            "Ready for pickup or delivery in 2–3 business days",
          ]}
          cta="BUILD MY PALLET"
          to="/build-pallet"
          gold
        />
      </div>

      <div className="mt-8 max-w-3xl">
        <PalletFinalSale />
      </div>
    </div>
  );
}
